import { type AssetClass, type SearchCollectionImagesQuery, useSearchCollectionImagesInfiniteQuery } from '@vizit/api';
import { type CollectionImageView, readPairScore, useCollectionPairRequestWatch } from '@vizit/api';
import { useInfiniteScroll } from '@vizit/hooks';
import { HEATMAP_ARTIFACT_TYPES } from '@vizit/organisms';
import { calculateOptimalLoadCount, debounce } from '@vizit/utils';
import * as React from 'react';
import { DEFAULT_ASSET_SORT, DEFAULT_SCORE_FIELD, isSameBenchmark } from './collection-asset-grid.state';
import { awaitsTileScores, isScoreForPair } from './collection-asset-grid.state';
import { mapClassificationToQueryParam, mapSortOptionToQueryParams } from './collection-asset-grid.state';
import type { CollectionAssetSortOption, CollectionGridScoring } from './collection-asset-grid.state';
import type { CollectionImageScoreField } from './collection-asset-grid.state';

const ARTIFACT_URLS_REFRESH_AFTER_MS = 50 * 60 * 1000;

/**
 * First page size that fills the viewport. Recomputed on window resize, and when the grid switches between responsive
 * and fixed columns (the feature flag can resolve after the first render, so `columns` arrives late on a cold load).
 * Not on every `columns` change: the count is the first page's `limit`, so that would refetch the whole grid while the
 * user drags the size slider. Later pages still come from infinite scroll.
 */
function useOptimalInitialLoadCount(columns?: number): number {
  const columnsRef = React.useRef(columns);
  columnsRef.current = columns;

  const hasFixedColumns = columns !== undefined;
  // Initialize with calculated value to avoid extra render
  const [loadCount, setLoadCount] = React.useState(() => ({
    count: calculateOptimalLoadCount(columns),
    hasFixedColumns,
  }));

  // Switched between responsive and fixed columns: recompute during render, not in an effect, so the grid never
  // fetches a page sized for the old columns first
  let { count } = loadCount;
  if (loadCount.hasFixedColumns !== hasFixedColumns) {
    count = calculateOptimalLoadCount(columns);
    setLoadCount({ count, hasFixedColumns });
  }

  React.useEffect(() => {
    // Debounced resize handler to limit calculations during window resizing
    const debouncedHandleResize = debounce(() => {
      const current = columnsRef.current;
      const next = { count: calculateOptimalLoadCount(current), hasFixedColumns: current !== undefined };
      // Same count, same object: no re-render on every resize step
      setLoadCount((prev) =>
        prev.count === next.count && prev.hasFixedColumns === next.hasFixedColumns ? prev : next,
      );
    }, 250); // 250ms debounce

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  return count;
}

interface UseCollectionAssetGridProps {
  /**
   * The ID of the portfolio
   */
  workspaceId: string;
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * Number of items to load per page
   * @default 12
   */
  limit?: number;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * How to sort the assets within the collection
   * @default CollectionAssetSortOption.DateNewest
   */
  sortOption?: CollectionAssetSortOption;
  /**
   * Which score field score-sort options use (e.g. `vizit_score` or `hero_score`)
   * @default 'vizit_score'
   */
  scoreField?: CollectionImageScoreField;
  /**
   * When true, generated (Spark) images are excluded
   * @default false
   */
  hideSparkImages?: boolean;
  /**
   * When set, only images of this asset class are returned; `undefined` returns all classes
   */
  classification?: AssetClass;
  tagIds?: string[];
  /**
   * Fixed images per row the grid renders, used to size the first page. `undefined` uses the responsive columns
   */
  columns?: number;
  /**
   * The pair to score the images against; unset reads the collection's own scores
   */
  scoring?: CollectionGridScoring;
  withArtifacts?: boolean;
}

/**
 * When the newest image on screen that waits for the pair to be requested again for it was uploaded, or `null`. The
 * page tracks an upload it saw (`waitingImageIds`), and the grid re-reads an image still waiting on its file, so only
 * the others count: an upload made before leaving the collection and coming back. A score kept on screen from the
 * previous pair while the new one loads is not this pair's, so it waits for nothing.
 */
function useNewestAwaitingUpload(scoring: CollectionGridScoring | undefined, assets: CollectionImageView[]) {
  const pair = scoring?.pair;
  const pairRequestedAt = scoring?.requestedAt;
  const waitingImageIds = scoring?.waitingImageIds;
  return React.useMemo(() => {
    if (!pair) {
      return null;
    }
    let newest: string | null = null;
    for (const image of assets) {
      const isOwnedElsewhere = !image.url || !!waitingImageIds?.includes(image.id);
      if (isOwnedElsewhere || !isScoreForPair(image.pair_score, pair) || !image.created_at) {
        continue;
      }
      const reading = readPairScore(image.pair_score, { imageCreatedAt: image.created_at, pairRequestedAt });
      if (reading.isAwaitingUpload && (!newest || Date.parse(image.created_at) > Date.parse(newest))) {
        newest = image.created_at;
      }
    }
    return newest;
  }, [pair, pairRequestedAt, waitingImageIds, assets]);
}

/**
 * Hook to fetch collection assets with infinite loading
 *
 * @param props - Hook configuration
 * @returns Collection assets data and loading state
 */
function useCollectionAssetGrid(props: UseCollectionAssetGridProps) {
  const {
    workspaceId,
    collectionId,
    limit = 12,
    useOptimalInitialLoad = true,
    sortOption,
    scoreField,
    hideSparkImages,
    classification,
    tagIds,
    columns,
    scoring,
    withArtifacts = false,
  } = props;

  const optimalInitialCount = useOptimalInitialLoadCount(columns);

  // Use optimal count for initial load, standard limit for subsequent pages
  const effectiveInitialLimit = useOptimalInitialLoad ? optimalInitialCount : limit;

  const { sort_by, sort_direction } = mapSortOptionToQueryParams(
    sortOption ?? DEFAULT_ASSET_SORT,
    scoreField ?? DEFAULT_SCORE_FIELD,
  );

  const { data, refetch, isLoading, error, hasNextPage, hasPreviousPage, isFetchingNextPage, fetchNextPage } =
    useSearchCollectionImagesInfiniteQuery(
      {
        workspace_id: workspaceId,
        collection_id: collectionId,
        limit: effectiveInitialLimit,
        sort_by,
        sort_direction,
        exclude_source: hideSparkImages ? ['generated'] : undefined,
        classification: mapClassificationToQueryParam(classification),
        tag_ids: tagIds?.length ? tagIds : undefined,
        include_artifacts: withArtifacts ? HEATMAP_ARTIFACT_TYPES : undefined,
        ...scoring?.pair,
      },
      {
        // With page-picked scores, wait until the page knows which pair, rather than read a list to throw away.
        enabled: !!workspaceId && !!collectionId && (!scoring || scoring.isResolved),
        // Keep the previous page's data (and total count) visible while a sort, spark, asset-class or category
        // change refetches, so the grid and "N assets" readout don't flash empty / "0". A new benchmark is a new
        // set of scores, so it skeleton-loads instead of showing the old benchmark's under the new name.
        placeholderData: (previousData, previousQuery) =>
          isSameBenchmark(previousQuery?.queryKey[1] as CollectionGridScoring['pair'], scoring?.pair)
            ? previousData
            : undefined,
        awaitsScores: awaitsTileScores(scoring),
      },
    );
  const isWaitingForScoring = !!scoring && !scoring.isResolved;

  const oldestPageFetchedAt = React.useMemo(() => {
    const pages = (data as { pages?: SearchCollectionImagesQuery[] } | undefined)?.pages ?? [];
    return Math.min(...pages.map((page) => page.fetchedAt ?? 0));
  }, [data]);

  const refreshExpiredArtifactUrls = React.useCallback(() => {
    if (Date.now() - oldestPageFetchedAt > ARTIFACT_URLS_REFRESH_AFTER_MS) {
      void refetch({ cancelRefetch: false });
    }
  }, [oldestPageFetchedAt, refetch]);

  const assets = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionImagesQuery[] } | undefined;
    const allImages = infiniteData?.pages?.flatMap((page) => page.data) || [];
    return allImages;
  }, [data]);

  // Once the pair is requested again for those uploads, the tiles and the page's scorings are read again.
  const awaitingSince = useNewestAwaitingUpload(scoring, assets);
  useCollectionPairRequestWatch({ workspaceId, collectionId, awaitingSince });

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionImagesQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  const sentinelRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });

  // Use hasPreviousPage to determine loading count
  // hasPreviousPage = false: initial load (show optimal count)
  // hasPreviousPage = true: subsequent loads (show standard limit)
  const loadingCount = React.useMemo(() => {
    if ((isLoading || isWaitingForScoring) && !hasPreviousPage) {
      // Initial load: show optimal initial count skeletons (e.g., 24)
      return effectiveInitialLimit;
    }
    if (isFetchingNextPage) {
      // Subsequent loads: show standard limit skeletons (e.g., 12)
      return limit;
    }
    // Not loading: no skeletons needed
    return 0;
  }, [isLoading, isWaitingForScoring, hasPreviousPage, isFetchingNextPage, effectiveInitialLimit, limit]);

  return {
    assets,
    totalCount,
    isLoading: isLoading || isWaitingForScoring,
    hasError: !!error,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
    refreshExpiredArtifactUrls,
  };
}

export { ARTIFACT_URLS_REFRESH_AFTER_MS, useCollectionAssetGrid };
export type { UseCollectionAssetGridProps };
